import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './cn';

/*
  Button — основной CTA-примитив (frontend/DESIGN.md §6, спека §3.2).
  Внешний вид задаётся только variant/size (R4); className снаружи — раскладка.

  primary     — градиент brand-strong → brand-deep (coral-700 → 800, в обеих темах): белый текст 5.3–7.1:1 (AA).
                coral-500/600 не проходят AA для текста (2.8 / 3.8:1) — см. спеку §2.2; коралл-500
                остаётся акцентом там, где текст не является содержанием.
  secondary   — карточка с рамкой; на hover рамка уходит, появляется коралловое кольцо.
  ghost       — прозрачная, hover-заливка.
  destructive — мягкий danger (soft + strong), на hover становится solid: опасное действие не должно
                быть самой громкой кнопкой и не должно путаться с primary.
  link        — текст брендового цвета, без рамок и высоты.
*/

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap select-none',
    'transition-interactive',
    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'active:scale-98',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-gradient-to-br from-brand-strong to-brand-deep text-on-brand shadow-brand hover:scale-103 hover:shadow-brand-lg hover:brightness-105 active:brightness-95',
        secondary:
          'border border-line-strong bg-card text-fg hover:scale-103 hover:border-transparent hover:bg-hover hover:shadow-glow-brand',
        ghost: 'bg-transparent text-fg hover:scale-103 hover:bg-hover hover:shadow-glow-brand',
        destructive:
          'border border-danger-line bg-danger-soft text-danger-strong hover:scale-103 hover:border-transparent hover:bg-danger hover:text-on-danger',
        link: 'text-brand-fg underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-4 text-sm',
        lg: 'h-11 px-6 text-base',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    compoundVariants: [{ variant: 'link', className: 'h-auto px-0' }],
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, Omit<VariantProps<typeof buttonVariants>, 'fullWidth'> {
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      size,
      loading = false,
      leftIcon,
      rightIcon,
      fullWidth,
      className,
      children,
      disabled,
      ...rest
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        data-slot="button"
        disabled={disabled || loading}
        className={cn(buttonVariants({ variant, size, fullWidth: fullWidth ? true : undefined }), className)}
        {...rest}
      >
        {loading && <ButtonSpinner size={size ?? 'md'} />}
        {!loading && leftIcon && <span className="-ml-0.5 [&_svg]:size-4">{leftIcon}</span>}
        {children}
        {!loading && rightIcon && <span className="-mr-0.5 [&_svg]:size-4">{rightIcon}</span>}
      </button>
    );
  },
);
Button.displayName = 'Button';

const spinnerSize: Record<ButtonSize, string> = {
  sm: 'size-3',
  md: 'size-3.5',
  lg: 'size-4',
};

// Спиннер внутри кнопки: currentColor, поэтому работает на любом варианте.
function ButtonSpinner({ size }: { size: ButtonSize }) {
  return (
    <span
      role="status"
      aria-label="Загрузка"
      className={cn(
        'inline-block animate-spin rounded-full border-2 border-current border-t-transparent opacity-75',
        spinnerSize[size],
      )}
    />
  );
}
